/**
 * Multi-tenant public sites: apex = Maphunziro marketing, each school = {slug}.PLATFORM_HOST
 *
 * Production DNS (example):
 *   edumanagemalawi.com, www → Netlify/hosting (SPA)
 *   *.edumanagemalawi.com     → same SPA (wildcard)
 *
 * Netlify preview (*.netlify.app): no wildcard subdomains — use /school/:slug on the deploy host.
 *
 * Env: REACT_APP_PLATFORM_HOST=edumanagemalawi.com
 *      REACT_APP_SCHOOL_SUBDOMAINS=false  (disable → path URLs /school/:slug only)
 */

export function isNetlifyAppHost(hostname) {
    return normalizeHostname(hostname).endsWith(".netlify.app");
}

const RESERVED_SUBDOMAINS = new Set([
    "www",
    "api",
    "app",
    "admin",
    "mail",
    "staging",
    "dev",
    "test",
    "platform",
    "login",
    "register",
    "schools",
    "school",
]);

function normalizePlatformHostInput(raw) {
    return String(raw)
        .trim()
        .toLowerCase()
        .replace(/^https?:\/\//, "")
        .replace(/\/.*$/, "")
        .replace(/^www\./, "")
        .replace(/:\d+$/, "");
}

/** Hostname used for subdomain DNS (custom domain). On Netlify preview, returns the deploy host. */
export function getPlatformHost() {
    if (typeof window !== "undefined") {
        const host = normalizeHostname(window.location.hostname);
        if (isNetlifyAppHost(host) || host === "localhost" || host === "127.0.0.1") {
            return host;
        }
    }
    return normalizePlatformHostInput(process.env.REACT_APP_PLATFORM_HOST || "edumanagemalawi.com");
}

/** Full site origin for links in the admin UI (current host on Netlify, configured domain in production). */
export function getSiteOrigin() {
    if (typeof window !== "undefined" && window.location?.origin) {
        return window.location.origin.replace(/\/$/, "");
    }
    const host = getPlatformHost();
    if (isNetlifyAppHost(host)) {
        return `https://${host}`;
    }
    return `https://www.${host}`;
}

export function isSchoolSubdomainsEnabled() {
    if (typeof window !== "undefined" && isNetlifyAppHost(window.location.hostname)) {
        return false;
    }
    return process.env.REACT_APP_SCHOOL_SUBDOMAINS !== "false";
}

/**
 * When false, `/` is always the Maphunziro marketing landing (not a school's public page).
 * Netlify preview has no wildcard subdomains; path URLs use /school/:slug for each school.
 */
export function usePlatformHomeSchoolOnApex() {
    if (isSchoolSubdomainsEnabled()) return false;
    if (typeof window !== "undefined" && isNetlifyAppHost(window.location.hostname)) {
        return false;
    }
    return true;
}

export function normalizeHostname(hostname) {
    return String(hostname || "")
        .trim()
        .toLowerCase()
        .split(":")[0];
}

export function isPlatformApexHost(hostname) {
    const host = normalizeHostname(hostname);
    if (isNetlifyAppHost(host)) return true;
    const platform = getPlatformHost();
    if (host === platform || host === `www.${platform}`) return true;
    if (host === "localhost" || host === "127.0.0.1") return true;
    return false;
}

/**
 * School tenant slug from hostname, e.g. zengwani.edumanagemalawi.com → "zengwani".
 * Local dev: zengwani.localhost (map hosts file or use path URLs).
 */
export function getSchoolSubdomainFromHost(hostname) {
    if (!isSchoolSubdomainsEnabled()) return null;
    const host = normalizeHostname(hostname);
    const platform = getPlatformHost();

    const suffix = `.${platform}`;
    if (host.endsWith(suffix)) {
        const sub = host.slice(0, -suffix.length);
        if (!sub || sub.includes(".") || RESERVED_SUBDOMAINS.has(sub)) return null;
        return sub;
    }

    if (host.endsWith(".localhost") && host !== "localhost") {
        const sub = host.slice(0, -".localhost".length);
        if (!sub || sub.includes(".") || RESERVED_SUBDOMAINS.has(sub)) return null;
        return sub;
    }

    return null;
}

export function getPlatformOrigin() {
    if (typeof window !== "undefined" && window.location?.origin) {
        const host = normalizeHostname(window.location.hostname);
        if (isNetlifyAppHost(host) || isPlatformApexHost(host) || getSchoolSubdomainFromHost(host)) {
            return getSiteOrigin();
        }
    }
    const platform = getPlatformHost();
    if (isNetlifyAppHost(platform)) {
        return `https://${platform}`;
    }
    return `https://www.${platform}`;
}

export function buildSchoolPublicOrigin(slug) {
    if (!isSchoolSubdomainsEnabled()) return null;
    const s = String(slug || "")
        .trim()
        .toLowerCase();
    if (!s) return null;
    const platform = getPlatformHost();
    if (typeof window !== "undefined") {
        const host = normalizeHostname(window.location.hostname);
        const port = window.location.port ? `:${window.location.port}` : "";
        if (isNetlifyAppHost(host)) return null;
        if (host === "localhost" || host === "127.0.0.1") {
            return `${window.location.protocol}//${s}.localhost${port}`;
        }
        if (host.endsWith(".localhost")) {
            return `${window.location.protocol}//${s}.localhost${port}`;
        }
        return `${window.location.protocol}//${s}.${platform}`;
    }
    return `https://${s}.${platform}`;
}
